"use client";

import { Suspense, type ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MessageSquare, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FluidOrb } from "@/components/ui/fluid-orb";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { isCommandPanelDisabledRoute } from "@/utils/command-panel-routes";
import { CommandSessionProvider } from "@/components/aios-command";
import { CommandV2ApiProvider } from "@/components/command-team-v2/providers/command-v2-api-provider";
import { CommandV2AssistantPanel } from "@/components/command-team-v2/chat/command-v2-assistant-panel";
import { useCommandPanel } from "./command-panel-provider";

function CommandPanelShell({ children }: { children: ReactNode }) {
    const { close } = useCommandPanel();

    return (
        <aside
            style={{ top: "var(--command-top, 3rem)" }}
            className="fixed bottom-0 right-0 z-40 flex w-full flex-col border-l bg-background shadow-xl animate-in slide-in-from-right duration-300 sm:w-[min(var(--command-width,400px),100vw)]"
        >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
                <p className="truncate px-2 text-sm font-semibold">Talk to Command</p>
                <button
                    type="button"
                    onClick={close}
                    aria-label="Close Command"
                    className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <X className="size-4" />
                </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
        </aside>
    );
}

export function CommandPanel() {
    const { isOpen, close, activeAgent, isLoadingActiveAgent, canListAgents } = useCommandPanel();
    const pathname = usePathname();

    const isLegacyCommandTeamRoute =
        pathname?.startsWith("/workforce/command-team-old") ?? false;
    const isCommandTeamV2Route =
        ((pathname === "/workforce/command-team" ||
            pathname?.startsWith("/workforce/command-team/")) ??
            false) &&
        !pathname.startsWith("/workforce/command-team-old");

    if (
        !isOpen ||
        isLegacyCommandTeamRoute ||
        isCommandTeamV2Route ||
        isCommandPanelDisabledRoute(pathname)
    ) {
        return null;
    }

    if (!canListAgents) {
        return (
            <CommandPanelShell>
                <div className="p-4">
                    <AccessDeniedCard message="You don't have permission to view Command agents. Contact your administrator if you need access." />
                </div>
            </CommandPanelShell>
        );
    }

    if (isLoadingActiveAgent) {
        return (
            <CommandPanelShell>
                <div className="flex h-full items-center justify-center">
                    <FluidOrb size="md" speed="fast" />
                </div>
            </CommandPanelShell>
        );
    }

    if (!activeAgent) {
        return (
            <CommandPanelShell>
                <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
                    <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                        <MessageSquare className="size-8 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold">No active agents found</h2>
                        <p className="max-w-sm text-sm text-muted-foreground">
                            Create or activate an agent to start chatting.
                        </p>
                    </div>
                    <Button asChild variant="outline" size="sm" onClick={close}>
                        <Link href="/workforce/command-team/agents">View agents</Link>
                    </Button>
                </div>
            </CommandPanelShell>
        );
    }

    return (
        <CommandSessionProvider agentId={activeAgent.id}>
            <Suspense fallback={null}>
                <CommandV2ApiProvider>
                    <CommandV2AssistantPanel />
                </CommandV2ApiProvider>
            </Suspense>
        </CommandSessionProvider>
    );
}
